class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(() => {
      this.classList.add('animate', 'active');
      // Under reduced motion, schedule the focus trap only once 'active' is set.
      if (reduceMotion) requestAnimationFrame(() => requestAnimationFrame(() => trapOnce()));
    });

    // Trap focus once the open transition ends. Under prefers-reduced-motion the
    // transition is ~0ms and transitionend may not fire, so a timeout runs the same
    // step (once) as a fallback.
    let trapped = false;
    let waitFrames = 0;
    const trapOnce = (evt) => {
      if (trapped) return;
      // Reduced motion: if 'active' is not on yet, retry next frame (bounded) instead of giving up.
      if (reduceMotion && !this.classList.contains('active') && waitFrames++ < 10) {
        requestAnimationFrame(() => trapOnce());
        return;
      }
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      // An early transitionend (e.g. a child's visibility step) can arrive before the
      // drawer is visible; focus() would silently fail, so wait for the next signal.
      if (evt && focusElement && getComputedStyle(focusElement).visibility === 'hidden') return;
      trapped = true;
      this.removeEventListener('transitionend', trapOnce);
      clearTimeout(fallbackTimer);
      if (!this.classList.contains('active')) return;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', trapOnce);
    // Transitions are disabled under reduced motion, so transitionend never fires: finish next frame.
    const fallbackTimer = reduceMotion ? null : setTimeout(trapOnce, 300);

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // The empty state class lives on the <cart-drawer> element itself (and on the inner drawer in older markup).
    this.classList.remove('is-empty');
    var drawerInnerEl = this.querySelector('.drawer__inner');
    if (drawerInnerEl && drawerInnerEl.classList.contains('is-empty')) {
      drawerInnerEl.classList.remove('is-empty');
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
